import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { resolveImg } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImagePlus, RotateCcw } from "lucide-react";

const FIELDS = ["offer_name", "show_offer_name", "heading", "heading_highlight", "subheading", "description", "supporting_line", "cta_text", "cta_link", "status"];

function F({ label, k, form, upd, area, hint }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {area ? <Textarea rows={2} value={form[k] ?? ""} onChange={e => upd(k, e.target.value)} data-testid={`featured-field-${k}`} />
        : <Input value={form[k] ?? ""} onChange={e => upd(k, e.target.value)} data-testid={`featured-field-${k}`} />}
      {hint && <p className="text-[11px] text-[#9C928D] mt-1">{hint}</p>}
    </div>
  );
}

function ImageBox({ url, onUpload, onRemove, busy }) {
  return (
    <div className="soft-card !p-5" data-testid="featured-image-panel">
      <div className="font-medium text-sm">Optional Featured Image</div>
      <p className="text-[11px] text-[#9C928D]">JPEG / PNG / WEBP · max 8MB · portrait works best. If empty, the Umesh Misra photo is shown.</p>
      {url ? <img src={resolveImg(url)} alt="" className="mt-3 rounded-xl max-h-56 object-cover" data-testid="featured-image-preview" />
        : <div className="mt-3 text-sm text-[#9C928D]">Using default photo.</div>}
      <div className="mt-3 flex gap-2 flex-wrap">
        <label className={`btn-ghost text-xs !px-4 !py-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          <ImagePlus size={14} className="mr-2" />{busy ? "Uploading…" : url ? "Replace Image" : "Upload Image"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="featured-image-upload" onChange={e => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {url && <button type="button" onClick={onRemove} className="btn-ghost text-xs !px-4 !py-2 !text-red-700" data-testid="featured-image-remove">Remove Image</button>}
      </div>
    </div>
  );
}

export default function HomepageFeaturedAdminPage() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/admin/homepage-featured").then(r => { if (live) setForm(f => f || r.data); }).catch(e => toast.error(formatErr(e)));
    return () => { live = false; };
  }, []);
  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const save = async (payload) => {
    try { const { data } = await api.put("/admin/homepage-featured", payload); setForm(s => ({ ...s, ...data })); toast.success("Homepage updated"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/homepage-featured/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(s => ({ ...s, ...data })); toast.success("Image saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Restore the original Meditation Enlightenment homepage content?")) return;
    try { const { data } = await api.post("/admin/homepage-featured/reset"); setForm(s => ({ ...s, ...data })); toast.success("Default content restored"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!form) return null;
  const p = { form, upd };
  return (
    <div className="space-y-6 fade-up" data-testid="admin-homepage-featured">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Homepage Featured Offer</h1>
          <p className="text-sm text-[#7E736E] max-w-2xl">Controls only the top section of the homepage. It never changes the programs themselves.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="btn-ghost text-sm" data-testid="featured-reset"><RotateCcw size={14} className="mr-2" />Restore default</button>
          <a href="/" target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="featured-open-home">Open Homepage</a>
        </div>
      </div>
      <div className="grid xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
        <div className="soft-card !p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <F label="Offer / Program Name" k="offer_name" {...p} />
            <label className="flex items-center gap-3 text-sm pb-2"><Switch checked={!!form.show_offer_name} onCheckedChange={v => upd("show_offer_name", v)} data-testid="featured-field-show_offer_name" />Show name as a small label above the heading</label>
          </div>
          <F label="Main Heading" k="heading" {...p} area />
          <F label="Main Heading — highlighted part (italic, coloured)" k="heading_highlight" {...p} hint="Optional. Shown right after the main heading." />
          <F label="Subheading" k="subheading" {...p} area />
          <F label="Description" k="description" {...p} area hint="Optional. Leave empty to hide." />
          <F label="Supporting line" k="supporting_line" {...p} hint="Optional. Leave empty to hide." />
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="CTA Button Text" k="cta_text" {...p} />
            <F label="CTA Destination" k="cta_link" {...p} hint="e.g. #enlightenment-form, /meditations, /bliss-path or https://…" />
          </div>
          <div className="sm:w-1/3">
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={v => upd("status", v)}>
              <SelectTrigger data-testid="featured-field-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive (show default)</SelectItem></SelectContent>
            </Select>
          </div>
          <button onClick={() => save(Object.fromEntries(FIELDS.map(k => [k, form[k]])))} className="btn-primary" data-testid="featured-save">Save changes</button>
        </div>
        <ImageBox url={form.image_url} busy={busy} onUpload={upload} onRemove={() => save({ image_url: "" })} />
      </div>
    </div>
  );
}
